import React, { useState, useEffect } from 'react';
import { Card, Badge, Nav, InputGroup, Form, Button, ListGroup } from 'react-bootstrap';

const EquipmentSelector = ({
  mode,
  setMode,
  filter,
  setFilter,
  totalEligibleCount,
  selectedEquipments,
  filteredEligible,
  handleToggleEquipment,
  selectAllFilteredEquipments,
  clearFilteredEquipments,
  visibleRegionsTotal,
  selectAllRegionsVisible,
  clearRegionsVisible,
  getRegionKeys,
  normalized,
  getRegionName,
  getRegionEquipments,
  getRegionSelectedCount,
  handleToggleRegion,
  expanded,
  toggleExpand,
  submitting,
  handleSendUpdate
}) => {
  const [theme, setTheme] = useState(() => {
    const bodyHasDarkClass = document.body.classList.contains('dark-theme');
    const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');
    return bodyHasDarkClass || htmlHasDarkClass ? 'dark' : 'light';
  });

  useEffect(() => {
    const handleThemeChange = () => {
      const bodyHasDarkClass = document.body.classList.contains('dark-theme');
      const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');
      setTheme(bodyHasDarkClass || htmlHasDarkClass ? 'dark' : 'light');
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  return (
    <Card className="mb-3 mt-2" style={{ minHeight: 'calc(100vh - 120px)' }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fw-bold mb-0" style={{ color: 'var(--brand-dark-green)' }}>
            Liberar equipamentos para atualização de curvas
          </h4>
          <Badge pill bg="secondary">{selectedEquipments.size} selecionado(s)</Badge>
        </div>

        <div
          className="mb-3 p-3 rounded"
          style={{
            backgroundColor: theme === 'dark' ? '#273036' : '#f8f9fa',
            border: theme === 'dark' ? '1px solid #39464f' : '1px solid #e9ecef'
          }}
        >
          <div className="d-flex flex-wrap gap-2 mb-2">
            <Badge bg="secondary">Elegíveis SIMT: {totalEligibleCount}</Badge>
            <Badge bg="light" text="dark">Selecionados: {selectedEquipments.size}</Badge>
          </div>
          <div
            style={{
              color: theme === 'dark' ? '#ced4da' : '#6c757d',
              fontSize: '0.9rem'
            }}
          >
            A seleção abaixo considera todos os equipamentos SIMT elegíveis para atualização de curvas.
          </div>
        </div>

        <Nav 
          variant="pills" 
          activeKey={mode} 
          onSelect={(k) => setMode(k)} 
          className="mb-3"
          style={{
            '--bs-nav-pills-link-active-bg': theme === 'dark' ? '#198754' : 'var(--brand-light-green, #8fad15)',
            '--bs-nav-pills-link-active-color': '#ffffff'
          }}
        >
          <Nav.Item>
            <Nav.Link 
              eventKey="equipment"
              className={mode !== 'equipment' ? (theme === 'dark' ? 'text-white' : 'text-dark') : ''}
              style={mode !== 'equipment' ? {
                backgroundColor: theme === 'dark' ? '#343a40' : '#8fad15',
                border: theme === 'dark' ? '1px solid #6c757d' : '1px solid #adb5bd',
                fontWeight: '600'
              } : {
                fontWeight: '600'
              }}
            >
              Por Equipamento
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              eventKey="state"
              className={mode !== 'state' ? (theme === 'dark' ? 'text-white' : 'text-dark') : ''}
              style={mode !== 'state' ? {
                backgroundColor: theme === 'dark' ? '#343a40' : '#8fad15',
                border: theme === 'dark' ? '1px solid #6c757d' : '1px solid #adb5bd',
                fontWeight: '600'
              } : {
                fontWeight: '600'
              }}
            >
              Por Estado
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              eventKey="city"
              className={mode !== 'city' ? (theme === 'dark' ? 'text-white' : 'text-dark') : ''}
              style={mode !== 'city' ? {
                backgroundColor: theme === 'dark' ? '#343a40' : '#8fad15',
                border: theme === 'dark' ? '1px solid #6c757d' : '1px solid #adb5bd',
                fontWeight: '600'
              } : {
                fontWeight: '600'
              }}
            >
              Por Cidade
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              eventKey="unit"
              className={mode !== 'unit' ? (theme === 'dark' ? 'text-white' : 'text-dark') : ''}
              style={mode !== 'unit' ? {
                backgroundColor: theme === 'dark' ? '#343a40' : '#8fad15',
                border: theme === 'dark' ? '1px solid #6c757d' : '1px solid #adb5bd',
                fontWeight: '600'
              } : {
                fontWeight: '600'
              }}
            >
              Por Unidade
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <InputGroup className="mb-2">
          <InputGroup.Text>Filtrar</InputGroup.Text>
          <Form.Control
            placeholder={mode === 'equipment' ? 'Serial ou versão' : mode === 'unit' ? 'Nome da unidade' : 'Nome da região'}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          {filter && (
            <Button 
              variant={theme === 'dark' ? 'outline-secondary' : 'outline-dark'} 
              onClick={() => setFilter('')}
            >
              Limpar
            </Button>
          )}
        </InputGroup>

        {mode === 'equipment' ? (
          <div className="d-flex justify-content-end gap-2 mb-2">
            <Button 
              size="sm" 
              variant={theme === 'dark' ? 'outline-light' : 'outline-primary'} 
              onClick={selectAllFilteredEquipments} 
              disabled={!filteredEligible.length}
            >
              Selecionar todos ({filteredEligible.length})
            </Button>
            <Button 
              size="sm" 
              variant={theme === 'dark' ? 'outline-secondary' : 'outline-dark'} 
              onClick={clearFilteredEquipments} 
              disabled={!filteredEligible.length}
            >
              Limpar seleção
            </Button>
          </div>
        ) : (
          <div className="d-flex justify-content-end gap-2 mb-2">
            <Button 
              size="sm" 
              variant={theme === 'dark' ? 'outline-light' : 'outline-primary'} 
              onClick={selectAllRegionsVisible} 
              disabled={!visibleRegionsTotal}
            >
              Selecionar todos ({visibleRegionsTotal})
            </Button>
            <Button 
              size="sm" 
              variant={theme === 'dark' ? 'outline-secondary' : 'outline-dark'} 
              onClick={clearRegionsVisible} 
              disabled={!visibleRegionsTotal}
            >
              Limpar seleção
            </Button>
          </div>
        )}

        {mode === 'equipment' ? (
          <>
            <div className="d-flex align-items-center justify-content-end mb-2">
              <Badge bg="light" text="dark">
                {filteredEligible.reduce((acc, eq) => acc + (selectedEquipments.has(eq.id) ? 1 : 0), 0)} / {filteredEligible.length}
              </Badge>
            </div>
            <ListGroup style={{ maxHeight: 500, overflowY: 'auto' }}>
              {filteredEligible.map((eq) => (
                <ListGroup.Item key={eq.id} className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      className="me-2"
                      checked={selectedEquipments.has(eq.id)}
                      onChange={(e) => handleToggleEquipment(eq.id, e.target.checked)}
                    />
                    <div>
                      <div className="fw-bold">{eq.id}</div>
                      <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                        {eq.city || 'Cidade N/I'} - {eq.state || 'UF N/I'} • Versão atual: {eq.currentVersion || 'N/I'}
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
              {!filteredEligible.length && (
                <div className="text-center text-muted py-4">Nenhum equipamento encontrado.</div>
              )}
            </ListGroup>
          </>
        ) : (
          <ListGroup style={{ maxHeight: 500, overflowY: 'auto' }}>
            {getRegionKeys()
              .filter((k) => {
                const nameMatches = normalized(getRegionName(k)).includes(normalized(filter));
                if (nameMatches) return true;
                return getRegionEquipments(k).length > 0;
              })
              .sort()
              .map((key) => {
                const total = getRegionEquipments(key).length;
                const selectedCount = getRegionSelectedCount(key);
                const fully = total > 0 && selectedCount === total;
                return (
                  <ListGroup.Item key={key}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <Form.Check
                          type="checkbox"
                          className="me-2"
                          checked={fully}
                          onChange={(e) => handleToggleRegion(key, e.target.checked)}
                          disabled={total === 0}
                        />
                        <div>
                          <div className="fw-bold">{getRegionName(key)}</div>
                          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                            {selectedCount} / {total} selecionado(s)
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <Badge bg="light" text="dark" className="me-2">{total}</Badge>
                        <Button 
                          size="sm" 
                          variant={theme === 'dark' ? 'outline-light' : 'outline-primary'} 
                          onClick={() => toggleExpand(key)}
                        >
                          {expanded.has(key) ? 'Recolher' : 'Expandir'}
                        </Button>
                      </div>
                    </div>
                    {expanded.has(key) && (
                      <div className="mt-2">
                        {(getRegionEquipments(key) || []).map((eq) => (
                          <div key={eq.id} className="d-flex align-items-center py-1">
                            <Form.Check
                              type="checkbox"
                              className="me-2"
                              checked={selectedEquipments.has(eq.id)}
                              onChange={(e) => handleToggleEquipment(eq.id, e.target.checked)}
                            />
                            <div>
                              <div className="fw-bold">{eq.id}</div>
                              <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                                {eq.city || 'Cidade N/I'} - {eq.state || 'UF N/I'} • Versão atual: {eq.currentVersion || 'N/I'}
                              </div>
                            </div>
                          </div>
                        ))}
                        {!getRegionEquipments(key).length && (
                          <div className="text-muted">Nenhum equipamento elegível nesta região.</div>
                        )}
                      </div>
                    )}
                  </ListGroup.Item>
                );
              })}
          </ListGroup>
        )}

      </Card.Body>
      <Card.Footer>
        <Button variant="success" onClick={handleSendUpdate} disabled={submitting || !selectedEquipments.size}>
          {submitting ? 'Salvando...' : 'Salvar liberação'}
        </Button>
      </Card.Footer>
    </Card>
  );
};

export default EquipmentSelector;